import { useRef } from "react";
import PropTypes from "prop-types";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box, Button, Chip } from "@mui/material";

/**
 * Attach one or more screenshots. Renders a dashed drop-target button and a chip
 * per selected file.
 *
 * **This used to be a `TextField` with its input hidden by CSS** — an outlined
 * text box, notched label and all, that never accepted text and existed only to
 * be clicked. Three things followed from that. It announced itself as a textbox,
 * so anyone navigating by control type was offered an editable field that could
 * not be typed into. It nested an icon button inside that field, which is a
 * control inside a control. And its notched label read "Attach screenshots"
 * directly beneath a "Screenshots" heading, so the same field was named twice in
 * two different styles.
 *
 * It is now the same dashed-button pattern the resume upload already used, so
 * the two file pickers in the app look and behave alike.
 */

const dashedButtonSx = {
  borderStyle: "dashed",
  borderColor: "divider",
  color: "text.secondary",
  textTransform: "none",
  fontWeight: 600,
  py: 1,
  borderRadius: 2,
  "&:hover": {
    // Repeat the style: MUI's hover rule resets it to solid otherwise.
    borderStyle: "dashed",
    borderColor: "primary.main",
    backgroundColor: "primary.extraLight",
    color: "primary.extraDark",
  },
};

const ImageUploadButton = ({
  selectedFiles,
  setSelectedFiles,
  selectedFileNames,
  setSelectedFileNames,
}) => {
  const inputRef = useRef(null);

  const handleUploadClick = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const newFile = files[0];
    setSelectedFiles((prev) => [...prev, newFile]);
    setSelectedFileNames((prev) => [...prev, newFile.name]);
    event.target.value = ""; // allow re-upload of the same file
  };

  const handleDelete = (label) => {
    const delIndex = selectedFileNames.indexOf(label);
    if (delIndex === -1) return;

    const newFiles = [...selectedFiles];
    const newNames = [...selectedFileNames];
    newFiles.splice(delIndex, 1);
    newNames.splice(delIndex, 1);

    setSelectedFiles(newFiles);
    setSelectedFileNames(newNames);
  };

  const hasFiles = selectedFileNames.length > 0;

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        startIcon={<AddPhotoAlternateIcon />}
        onClick={() => inputRef.current?.click()}
        sx={dashedButtonSx}
      >
        {hasFiles ? "Add another screenshot" : "Attach screenshots"}
      </Button>

      {hasFiles && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
          {selectedFileNames.map((name) => (
            <Chip
              key={name}
              label={name}
              size="small"
              variant="filled"
              onDelete={() => handleDelete(name)}
              // titleAccess names the delete control. A bare MUI SvgIcon is
              // aria-hidden by default, so an aria-label on it is discarded —
              // see docs/accessibility.md.
              deleteIcon={<CancelIcon titleAccess={`Remove ${name}`} />}
              sx={{
                bgcolor: "primary.extraLight",
                color: "primary.extraDark",
                fontWeight: 600,
                fontSize: "0.7rem",
                maxWidth: "100%",
                "& .MuiChip-deleteIcon": {
                  color: "primary.main",
                  "&:hover": { color: "primary.extraDark" },
                },
              }}
            />
          ))}
        </Box>
      )}

      <input
        ref={inputRef}
        accept="image/*"
        id="contained-button-file"
        multiple
        type="file"
        onChange={handleUploadClick}
        hidden
      />
    </Box>
  );
};

ImageUploadButton.propTypes = {
  selectedFiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSelectedFiles: PropTypes.func.isRequired,
  selectedFileNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedFileNames: PropTypes.func.isRequired,
};

export default ImageUploadButton;
